import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, BadgeCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { fetchPafCase } from '@/services/anatel';
import type { PafCaseDecision, PafComparable } from '@/services/types';

export function AnatelPafCaseDetailPage() {
  const { t } = useTranslation(['anatel', 'common']);
  const { key = '' } = useParams();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'paf', 'case', key],
    queryFn: () => fetchPafCase(key),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={3} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <BackLink />
        <ErrorMessage
          message={notFound ? t('anatel:pafCase.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <BackLink />

      <header className="space-y-1">
        <h1 className="text-lg font-semibold tabular-nums text-foreground">
          {data.process_number}
        </h1>
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {data.entity_name ?? (
            <span className="italic">{t('anatel:pafCase.noEntity')}</span>
          )}
          {data.entity_cnpj ? <span className="tabular-nums">· {data.entity_cnpj}</span> : null}
          {data.entity_cnpj_valid ? (
            <span className="inline-flex items-center gap-1 text-xs">
              <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
              {t('anatel:process.cnpjValid')}
            </span>
          ) : null}
        </p>
      </header>

      <ExplainerCard i18nKey="anatel:pafCase.explainer" />

      <FieldGuide i18nKey="anatel:pafCase.guide" level={2} />

      {/* Repeated per screen, not only on the overview: a reader can land here
          from a search result without ever seeing the corpus warning. */}
      <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground">
        <AlertTriangle
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden
        />
        <p>{t('anatel:paf.separateCorpus')}</p>
      </div>

      <AsOfStrip asOf={data.as_of} />

      <ol className="space-y-3">
        {data.decisions.map((decision, index) => (
          <li key={decision.decision_number ?? index}>
            <PafDecisionCard
              decision={decision}
              comparables={data.comparables[decision.decision_number ?? ''] ?? []}
              comparableTotal={data.comparable_totals[decision.decision_number ?? ''] ?? 0}
              reachMeasured={data.comparator_reach_measured}
              reachTotal={data.comparator_reach_total}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation(['anatel']);
  return (
    <Link
      to="/anatel/paf/decisions"
      className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t('anatel:pafCase.backToList')}
    </Link>
  );
}

function PafDecisionCard({
  decision,
  comparables,
  comparableTotal,
  reachMeasured,
  reachTotal,
}: {
  decision: PafCaseDecision;
  comparables: PafComparable[];
  comparableTotal: number;
  reachMeasured: number;
  reachTotal: number;
}) {
  const { t, i18n } = useTranslation(['anatel']);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });

  return (
    <article className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">
          {decision.decision_number ?? decision.decision_type ?? '—'}
        </h2>
        <span className="text-xs tabular-nums text-muted-foreground">
          {decision.reference_date_raw ?? '—'}
          {decision.is_final_observed ? ` · ${t('anatel:process.latestObserved')}` : ''}
        </span>
      </div>

      <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
        {decision.relator ? (
          <Field label={t('anatel:paf.relator')} value={decision.relator} />
        ) : null}
        {decision.forum_deliberativo ? (
          <Field label={t('anatel:pafCase.forum')} value={decision.forum_deliberativo} />
        ) : null}
        {decision.deciding_authority ? (
          <Field label={t('anatel:pafCase.authority')} value={decision.deciding_authority} />
        ) : null}
      </dl>

      {/* `relator` on this track IS a named person -- the only place in the ANATEL
          corpus where a person and an outcome share a row. `deciding_authority`
          beside it is not, and the two rendering identically is what invites the
          confusion. */}
      {decision.deciding_authority ? (
        <p className="text-xs italic text-muted-foreground">
          {t('anatel:pafCase.authorityOffice')}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-1.5">
        {(decision.outcome_codes.length === 0 ? ['UNCLASSIFIED'] : decision.outcome_codes).map(
          (code) => (
            <span key={code} className="rounded border px-2 py-0.5 text-xs text-muted-foreground">
              {t(`anatel:paf.outcomes.${code}`, { defaultValue: code })}
            </span>
          ),
        )}
        {decision.credit_amount_brl !== null ? (
          <span className="rounded border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs tabular-nums text-foreground">
            {t('anatel:pafCase.creditLabel', {
              amount: currency.format(decision.credit_amount_brl),
            })}
          </span>
        ) : null}
      </div>

      {decision.legal_citations.length > 0 ? (
        <section className="space-y-1">
          <h3 className="text-xs font-medium text-foreground">{t('anatel:process.citations')}</h3>
          <ul className="flex flex-wrap gap-1.5">
            {decision.legal_citations.map((citation) => (
              <li
                key={citation}
                className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              >
                {citation}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {decision.ementa ? (
        <details>
          <summary className="cursor-pointer text-xs font-medium text-primary">
            {t('anatel:pafCase.ementa')}
          </summary>
          <p className="mt-2 whitespace-pre-wrap border-l-2 pl-3 text-xs text-muted-foreground">
            {decision.ementa}
          </p>
        </details>
      ) : null}

      <Comparables
        comparables={comparables}
        total={comparableTotal}
        reachMeasured={reachMeasured}
        reachTotal={reachTotal}
      />
    </article>
  );
}

/**
 * Comparable fiscal decisions — NOT an adjudicated inconsistency queue.
 *
 * The sanctioning track carries a model verdict on each flagged pair. This track
 * has no verdict layer at all, so a differing outcome here is a prompt to look
 * rather than a finding, and the panel says so before listing anything.
 */
function Comparables({
  comparables,
  total,
  reachMeasured,
  reachTotal,
}: {
  comparables: PafComparable[];
  total: number;
  reachMeasured: number;
  reachTotal: number;
}) {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });
  // Built HERE, from numbers. A sentence composed in the backend arrives in one
  // language and renders untranslated on a page in another -- visible on this
  // very panel before this change.
  const reach = t('anatel:pafCase.comparables.reach', {
    measured: number.format(reachMeasured),
    total: number.format(reachTotal),
    share: new Intl.NumberFormat(i18n.language, {
      style: 'percent',
      maximumFractionDigits: 1,
    }).format(reachTotal === 0 ? 0 : reachMeasured / reachTotal),
  });

  if (comparables.length === 0) {
    return (
      <section className="space-y-1 border-t pt-3">
        <h3 className="text-sm font-medium text-foreground">
          {t('anatel:pafCase.comparables.title')}
        </h3>
        <p className="text-xs text-muted-foreground">{t('anatel:pafCase.comparables.none')}</p>
        <p className="text-xs text-muted-foreground">{reach}</p>
      </section>
    );
  }

  return (
    <section className="space-y-2 border-t pt-3">
      <h3 className="text-sm font-medium text-foreground">
        {t('anatel:pafCase.comparables.title')}
      </h3>
      <p className="text-xs text-muted-foreground">{t('anatel:pafCase.comparables.notAQueue')}</p>

      <ul className="space-y-2">
        {comparables.map((c, index) => (
          <li key={`${c.process_key}-${c.decision_number ?? index}`} className="rounded-md border p-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {/* A differing outcome is the reason to look, so it is the first
                  thing on the row -- but worded as a prompt, never as a verdict. */}
              {c.same_outcome === false ? (
                <span className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-medium text-amber-700 dark:text-amber-300">
                  {t('anatel:pafCase.comparables.differs')}
                </span>
              ) : c.same_outcome === true ? (
                <span className="rounded border px-2 py-0.5 text-muted-foreground">
                  {t('anatel:pafCase.comparables.same')}
                </span>
              ) : (
                <span className="rounded border px-2 py-0.5 italic text-muted-foreground">
                  {t('anatel:pafCase.comparables.unclassified')}
                </span>
              )}
              <Link
                to={`/anatel/paf/c/${c.process_key}`}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {c.process_number}
              </Link>
              {c.entity_name ? (
                <span className="max-w-xs truncate text-muted-foreground">{c.entity_name}</span>
              ) : null}
              {c.reference_date_raw ? (
                <span className="text-muted-foreground">{c.reference_date_raw}</span>
              ) : null}
              <span className="ml-auto text-muted-foreground">
                {t('anatel:pafCase.comparables.shared', { count: c.shared_count })}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
              <span>
                {c.outcome_codes.length === 0
                  ? t('anatel:paf.outcomes.UNCLASSIFIED')
                  : c.outcome_codes
                      .map((code) => t(`anatel:paf.outcomes.${code}`, { defaultValue: code }))
                      .join(' · ')}
              </span>
              {/* Credit, never a fine. Same rule as everywhere on this track. */}
              {c.credit_amount_brl === null ? null : (
                <span className="tabular-nums">{currency.format(c.credit_amount_brl)}</span>
              )}
            </div>
          </li>
        ))}
      </ul>

      {total > comparables.length ? (
        <p className="text-xs text-muted-foreground">
          {t('anatel:pafCase.comparables.capped', {
            shown: number.format(comparables.length),
            total: number.format(total),
          })}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">{reach}</p>
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}
